import { Button, Grid, Modal, Popover, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BaseResponse from "../../app/BaseResponse";
import Utility from "../../app/utility";
import { CountryModel } from "../Contact/models/ContactStaticResourcesModel";
import { contactInputSelector } from "../Contact/reducers/ContactInputReducer";
import { searchContactName } from "../ContactSelector/ContactSelector";
import Datatable, { PagingTypeEnum } from "../Datatable/Datatable";
import DatatableColumnModel from "../Datatable/DatatableColumnModel";
import { OrderDirection } from "../Datatable/OrderDirection";
import { showLoading } from "../LoadingView/reducers/LoadingViewReducer";
import NumberFormatCustom from "../NumberFormatCustom/NumberFormatCustom";
import AreaModel from "../PortSelector/models/AreaModel";
import PortMultipleSelector from "../PortSelector/PortMultipleSelector";
import { ComponentType } from "../SavedSearch/SavedSearch";
import TagsFilter from "../TagFilter/TagsFilter";
import DwtSelector from "../Tonnage/components/DwtSelector";
import { QueryItem } from "../Tonnage/models/TonnageQueryModel";
import MatchingApi from "./api/MatchingApi";
import { MarketingMatchingContactModel } from "./models/MarketingMatchingContactModel";
import { matchingPreviewSelector, setCompanyNames, setContactSearchKeyword, setDisAreas, setExcludedCompanies, setExcludedCountries, setLoadAreas, setMatchingData, setMatchingOrderBy, setMatchingOrderDirection, setOpenModal, setTriggerMarketingMatching } from "./reducers/MatchingPreviewReducer";


const MatchingPreview: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const matchingPreviewData = useSelector(matchingPreviewSelector);
    const [checkedRows, setCheckedRows] = useState<MarketingMatchingContactModel[]>([]);
    const contactInputViewData = useSelector(contactInputSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (matchingPreviewData.contactSearchKeyword && matchingPreviewData.contactSearchKeyword.length > 0) {
                searchContactName(matchingPreviewData.contactSearchKeyword, (contacts) => {
                    if (contacts && contacts.length > 0) {
                        dispatch(setCompanyNames(contacts));
                    } else {
                        dispatch(setCompanyNames([]));
                    }
                })
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [matchingPreviewData.contactSearchKeyword])

    const queryItemsToAeras = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                name: queryItem.value,
                positive: queryItem.positive
            } as AreaModel
        })
    }

    const columns = [
        {
            Field: "companyName",
            Title: "Company Name",
            AllowSort: true,
        } as DatatableColumnModel<MarketingMatchingContactModel>,
        {
            Field: "country",
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<MarketingMatchingContactModel>,
        {
            Field: "emails",
            Title: "Emails",
            AllowSort: true,
            Render: (rowData: MarketingMatchingContactModel) => {
                return <>
                    <Button
                        className="table-emails"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.emails && rowData.emails.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.emails);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.emails || rowData.emails.length === 0}
                    >
                        <i className="fa fa-envelope-o" aria-hidden="true"></i>
                    </Button>
                </>
            }
        } as DatatableColumnModel<MarketingMatchingContactModel>,
    ];

    useEffect(() => {
        if (matchingPreviewData.triggerMarketingMatching > 0) {
            dispatch(showLoading(true));

            let methodCall = matchingPreviewData.type === ComponentType.Cargo ?
                MatchingApi.MatketingMatchingTonnage(matchingPreviewData.matchingQuery) :
                MatchingApi.MatketingMatchingCargo(matchingPreviewData.matchingQuery);

            methodCall
                .then((response: BaseResponse<MarketingMatchingContactModel[]>) => {
                    if (response.success) {
                        dispatch(setMatchingData(response.data));
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                }).catch((error) => {
                    Utility.Alert.Error(error);
                })
                .finally(() => {
                    dispatch(showLoading(false));
                });
        }
    }, [matchingPreviewData.triggerMarketingMatching])

    const countryToQueryItems = (countries: CountryModel[]) => {
        return _.map(countries, (country) => {
            return {
                id: country.code,
                value: country.code,
                positive: true
            } as QueryItem
        })
    }

    const areasToQueryItems = (areas: AreaModel[]) => {
        return _.map(areas, (area) => {
            return {
                id: area.name,
                value: area.name,
                positive: area.positive
            } as QueryItem
        })
    }

    return (
        <>
            <Modal
                open={matchingPreviewData.openModal && matchingPreviewData.matchingQuery.marketingMatching}
                onClose={() => {
                    dispatch(setOpenModal(false));
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: {
                            lg: "800px",
                            xs: "90%"
                        },
                        bgcolor: 'white',
                        border: '2px solid #000',
                        boxShadow: 24,
                        p: 4,
                        maxHeight: 'calc(100vh - 100px)',
                        overflow: 'auto'
                    }}
                    className="nice-scroll"
                >
                    {
                        matchingPreviewData.type == ComponentType.Tonnage &&
                        <>
                            <PortMultipleSelector
                                portLabel='Port Open'
                                areaLabel='Loading Area'
                                areaValues={queryItemsToAeras(matchingPreviewData.matchingQuery.loadAreas)}
                                className="tonnage-search-item"
                                onlyAreaMode={true}
                                onAreaChange={(area: AreaModel[]) => {
                                    dispatch(setLoadAreas(areasToQueryItems(area)));
                                }}
                                useAreaBookmark={true}
                            >
                            </PortMultipleSelector>
                            <PortMultipleSelector
                                portLabel='Port Open'
                                areaLabel='Discharging Area'
                                areaValues={queryItemsToAeras(matchingPreviewData.matchingQuery.disAreas)}
                                className="tonnage-search-item"
                                onlyAreaMode={true}
                                onAreaChange={(area: AreaModel[]) => {
                                    dispatch(setDisAreas(areasToQueryItems(area)));
                                }}
                                useAreaBookmark={true}
                            >
                            </PortMultipleSelector>
                        </>
                    }
                    {
                        matchingPreviewData.type == ComponentType.Cargo &&
                        <DwtSelector
                            dwtFromValue={matchingPreviewData.matchingQuery.dwtMin}
                            dwtToValue={matchingPreviewData.matchingQuery.dwtMax}
                            className="tonnage-search-item"
                            dwtFromLabel='Dwt Min(kT)'
                            dwtToLabel='Dwt Max(kT)'
                            useSizeSelector={false}
                            disabled={true}
                        >
                        </DwtSelector>
                    }
                    {
                        matchingPreviewData.type == ComponentType.Tonnage &&
                        <TextField
                            fullWidth
                            className="tonnage-search-item"
                            label="Tonnage Dwt(kT)"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={matchingPreviewData.matchingQuery.tonnageDwt}
                            disabled={true}
                        >
                        </TextField>
                    }
                    <TagsFilter
                        data={countryToQueryItems(contactInputViewData.staticResources.countries)}
                        label="Exclude Countries"
                        className="tonnage-search-item"
                        value={matchingPreviewData.matchingQuery.excludedCountries}
                        hideReverseButton={true}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setExcludedCountries(value))
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(matchingPreviewData.companyNames, (item) => {
                            return {
                                id: item.id,
                                value: item.companyName,
                                positive: true
                            } as QueryItem
                        })}
                        hideReverseButton={true}
                        label="Exclude Companies"
                        onInputChange={(text: string) => {
                            dispatch(setContactSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        value={matchingPreviewData.matchingQuery.excludedCompanies}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setExcludedCompanies(value))
                        }}
                    ></TagsFilter>
                    <Grid
                        container
                        justifyContent="flex-end"
                        className="table-search-result-utility-bar"
                    >
                        <Grid>
                            <Button
                                color="secondary"
                                variant='outlined'
                                className='table-search-result-utility-bar-item table-marketing-result-utility-bar'
                                onClick={() => {
                                    dispatch(setTriggerMarketingMatching(new Date().getTime()));
                                }}
                            >Update</Button>
                            <Button
                                color="secondary"
                                variant='outlined'
                                className='table-search-result-utility-bar-item table-marketing-result-utility-bar'
                                onClick={() => {
                                    let emails = "";
                                    _.each(checkedRows, (item) => {
                                        emails += item.emails + ";";
                                    });
                                    navigator.clipboard.writeText(emails);
                                    Utility.Alert.Success("Copied to clipboard");
                                }}
                            >Copy emails to clipboard</Button>
                        </Grid>
                        <Grid
                            className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
                        >
                            {matchingPreviewData.datatable.length + " results found"}
                        </Grid>
                    </Grid>
                    <Datatable<MarketingMatchingContactModel>
                        useDefaultSearchBar={false}
                        pagingType={PagingTypeEnum.None}
                        data={matchingPreviewData.datatable}
                        useItemPerPage={false}
                        columns={columns}
                        isDataLoaded={true}
                        useHeaderSort={true}
                        stickyHeader={true}
                        useCheckBox={true}
                        onTableCheckboxSelectedChange={(selectedRows: MarketingMatchingContactModel[]) => {
                            setCheckedRows(selectedRows);
                        }}
                        onOrderDirectionChange={(column: DatatableColumnModel<MarketingMatchingContactModel>, orderDirection: OrderDirection) => {
                            dispatch(setMatchingOrderBy(column.Field));
                            dispatch(setMatchingOrderDirection(orderDirection));

                            let data = [...matchingPreviewData.datatable];
                            if (orderDirection == OrderDirection.Asc) {
                                data = _.sortBy(data, column.Field);
                            } else {
                                data = _.sortBy(data, column.Field).reverse();
                            }

                            dispatch(setMatchingData(data));
                        }}
                    ></Datatable>
                    <Popover
                        id="mouse-over-popover"
                        sx={{
                            pointerEvents: 'none',
                        }}
                        open={anchorEl != null}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        disableRestoreFocus
                    >
                        <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
                    </Popover>
                </Box>
            </Modal>
        </>
    )
};

export default MatchingPreview;